import * as Sentry from "@sentry/react";

declare global {
  interface Window {
    __sentryTestHooks?: {
      emitError: (marker: string) => Promise<string>;
      emitWarning: (marker: string) => Promise<string>;
      emitFeedback: (marker: string) => Promise<string>;
      emitMetric: (marker: string, value?: number) => Promise<string>;
    };
  }
}

const dsn = import.meta.env.VITE_SENTRY_DSN;
const environment = import.meta.env.VITE_SENTRY_ENVIRONMENT ?? import.meta.env.MODE;
const appVersion = __APP_VERSION__;
const appSha = __APP_SHA__;
const release = import.meta.env.VITE_SENTRY_RELEASE || `supplyspark-mkt@${appVersion}`;
const dist = import.meta.env.VITE_SENTRY_DIST ?? "local";
const tracesSampleRate = Number.parseFloat(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE ?? "0.1");
const replayEnabledOverride = import.meta.env.VITE_SENTRY_REPLAY_ENABLED;
const replayEnabled = replayEnabledOverride
  ? replayEnabledOverride === "true"
  : environment === "staging";
const configuredReplaySessionSampleRate = Number.parseFloat(import.meta.env.VITE_SENTRY_REPLAYS_SESSION_SAMPLE_RATE ?? "");
const configuredReplayOnErrorSampleRate = Number.parseFloat(import.meta.env.VITE_SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE ?? "");
const replaysSessionSampleRate = replayEnabled
  ? (Number.isFinite(configuredReplaySessionSampleRate)
      ? configuredReplaySessionSampleRate
      : (environment === "staging" ? 0.1 : 1.0))
  : 0;
const replaysOnErrorSampleRate = replayEnabled
  ? (Number.isFinite(configuredReplayOnErrorSampleRate) ? configuredReplayOnErrorSampleRate : 1.0)
  : 0;

function installSentryTestHooks(): void {
  if (typeof window === "undefined") {
    return;
  }

  const explicitTestHooksEnabled = import.meta.env.VITE_SENTRY_TEST_HOOKS === "true";
  const nonProductionEnvironment = environment === "preview" || environment === "staging";
  const allowTestHooks = explicitTestHooksEnabled || nonProductionEnvironment;
  if (!import.meta.env.DEV && !allowTestHooks) {
    return;
  }

  window.__sentryTestHooks = {
    emitError: async (marker: string): Promise<string> => {
      const id = Sentry.captureException(new Error(marker));
      await Sentry.flush(5000);
      return id;
    },
    emitWarning: async (marker: string): Promise<string> => {
      const id = Sentry.captureMessage(marker, "warning");
      await Sentry.flush(5000);
      return id;
    },
    emitFeedback: async (marker: string): Promise<string> => {
      const id = await Sentry.sendFeedback({
        name: "Local Test Runner",
        email: "local-feedback-test@supply-guard.io",
        message: marker,
      });
      return id;
    },
    emitMetric: async (marker: string, value = 1): Promise<string> => {
      const metricsApi = (Sentry as unknown as {
        metrics?: { gauge?: (name: string, value: number, options?: { tags?: Record<string, string> }) => void };
      }).metrics;

      if (metricsApi?.gauge) {
        metricsApi.gauge("sentry.local.test.metric", value, {
          tags: { marker, environment },
        });
      }

      const id = Sentry.captureMessage(`metric-smoke:${marker}`, "info");
      await Sentry.flush(5000);
      return id;
    },
  };
}

type FeatureErrorContext = {
  feature: string;
  action: string;
  endpoint?: string;
  status?: number;
  details?: Record<string, unknown>;
};

function toError(error: unknown, context: FeatureErrorContext): Error {
  if (error instanceof Error) {
    return error;
  }

  if (typeof error === "string" && error.trim()) {
    return new Error(error);
  }

  return new Error(`Feature failure in ${context.feature} during ${context.action}`);
}

export function captureFeatureError(error: unknown, context: FeatureErrorContext): string | undefined {
  if (!dsn) {
    return undefined;
  }

  const normalizedError = toError(error, context);

  return Sentry.withScope((scope) => {
    scope.setLevel("error");
    scope.setTag("feature_affecting", "true");
    scope.setTag("feature_name", context.feature);
    scope.setTag("feature_action", context.action);

    if (context.endpoint) {
      scope.setTag("feature_endpoint", context.endpoint);
    }
    if (typeof context.status === "number") {
      scope.setTag("http_status", String(context.status));
    }

    scope.setContext("feature_failure", {
      feature: context.feature,
      action: context.action,
      endpoint: context.endpoint ?? "",
      status: context.status ?? null,
      ...context.details,
    });

    if (!(error instanceof Error) && error !== undefined) {
      scope.setExtra("raw_error", error);
    }

    return Sentry.captureException(normalizedError);
  });
}

export function initSentry(): void {
  if (!dsn) {
    return;
  }

  Sentry.init({
    dsn,
    environment,
    release,
    dist,
    initialScope: {
      tags: {
        app_version: appVersion,
        app_sha: appSha,
      },
    },
    integrations: [
      Sentry.browserTracingIntegration(),
      ...(replayEnabled
        ? [Sentry.replayIntegration({
            maskAllText: true,
            blockAllMedia: true,
          })]
        : []),
      Sentry.feedbackIntegration({
        autoInject: true,
        showBranding: false,
        colorScheme: "light",
        triggerLabel: "Send feedback",
        formTitle: "Share feedback with the Supply Guard team",
        submitButtonLabel: "Submit feedback",
        messagePlaceholder: "What should we improve?",
      }),
    ],
    tracePropagationTargets: ["localhost", /^https:\/\/.*\.vercel\.app/, /^https:\/\/.*\.supabase\.co/],
    tracesSampleRate: Number.isFinite(tracesSampleRate) ? tracesSampleRate : 0.1,
    replaysSessionSampleRate: Number.isFinite(replaysSessionSampleRate) ? replaysSessionSampleRate : 0,
    replaysOnErrorSampleRate: Number.isFinite(replaysOnErrorSampleRate) ? replaysOnErrorSampleRate : 0,
  });

  installSentryTestHooks();
}
